import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Checkbox } from 'src/core/forms';
import { useDispatch } from 'react-redux';
import { useDeps } from 'src/hooks/useDeps';
import classnames from 'classnames';
import { debounceTime, distinctUntilChanged, map, skip, tap } from 'rxjs';
import { RxFormContext } from 'src/core/forms';
import { swatchCss, SwatchData } from 'src/components/ProductDetail/utils/swatchData';
import { StockStatus } from 'src/types/global-types';
import { QtySelect } from 'src/components/Forms/QtySelect';
import { useFormApi } from 'src/core/forms';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { Button } from 'src/components/Button/Button';
import { MIN_NUMBER_OF_COLORS_FOR_DROPDOWN } from 'src/constants/swatches';
import { useViewport } from 'src/hooks/useViewport';
import { ProductSizes } from 'src/components/ProductDetail/ProductSizes';
import { ProductVariant } from 'src/components/ProductDetail/utils/product-detail.types';
import { AlternateStock } from 'src/components/ProductDetail/AlternateStock/AlternateStock';
import { LazyLoader } from 'src/components/Helpers/LazyLoader';
import { StockNotifyModalProps } from 'src/components/StockNotify/StockNotify';
import { CallForStockAvailability } from 'src/components/ProductDetail/CallForStockAvailability';
import { ShippingGroups } from 'src/components/ProductDetail/ProductOptions';

import { Selector } from '../Selector/Selector';
import { getSwatchAccessibleName, SwatchOption } from '../Swatch/SwatchOption';

import classes from './ProductSwatch.scss';
import { ColourPicker } from './ColourPicker';

export interface Swatch {
    title: string;
    value_index: number;
    hex?: string;
    swatch_data: SwatchData;
    name?: string;
}

export interface CamelizedSwatch {
    title: string;
    valueIndex: number;
    hex?: string;
    swatchData: SwatchData;
    name?: string;
}

export interface Size {
    title: string;
    id: string | number;
    stock?: StockStatus;
    maxQty: number | null;
    simpleProductId: number | null;
    simpleProductSku: string | null;
}

enum Quantity {
    Minimum = 0,
    Maximum = 100,
}

type Props = {
    title?: string;
    count: number;
    swatches: Swatch[];
    sizes: Size[];
    isBundleView: boolean;
    setBundleColor?: React.Dispatch<React.SetStateAction<string[]>>;
    afterQuantity?: React.ReactNode;
    defaultColorPickerView?: string;
    defaultSizePickerView?: string;
    sizeGuide?: string;
    loading?: boolean;
    currentVariants?: ProductVariant[];
    selectCorrectShippingMethods?: (sku: string, value: number) => void;
    simpleProductsShippingGroups?: ShippingGroups;
    parentProductShippingGroup?: string;
    resetShippingGroups?: () => void;
};

const LazyStockNotifyModal: React.FC<StockNotifyModalProps> = React.lazy(
    () => import(/* webpackChunkName:"__lazy__stock-notify" */ '../StockNotify/StockNotify'),
);

export const sizeSelectItems = (
    sizes: Size[],
    initialSizeState: string | number,
    handleChangeSizeState: (id: string | number) => void,
) => {
    return sizes.map((size, index) => (
        <label
            htmlFor={`size-${size.id}`}
            key={index}
            onClick={() => handleChangeSizeState(size.id)}
            className={classnames(classes.itemLabelMobile, {
                [classes.itemLabelMobileActive]: size.id === initialSizeState,
            })}
        >
            <input
                name="size"
                value={size.id}
                id={`size-${size.id}`}
                checked={size.id === initialSizeState}
                type="radio"
                aria-label={getSwatchAccessibleName('Size', size.title)}
            />
            <span>{size.title}</span>
        </label>
    ));
};

export const notCustomizableSizesList = (sizes: Size[]) =>
    sizes.map((size) => ({
        name: size.title,
        value: '' + size.id,
        maxQty: size.maxQty,
    }));

export const ProductSwatch: React.FunctionComponent<Props> = ({
    afterQuantity,
    swatches,
    sizes,
    setBundleColor,
    isBundleView,
    title,
    defaultColorPickerView,
    defaultSizePickerView,
    sizeGuide,
    loading,
    currentVariants,
    selectCorrectShippingMethods,
    simpleProductsShippingGroups,
    parentProductShippingGroup,
    resetShippingGroups,
}) => {
    const { isMobile } = useViewport();
    const swatchTotal = swatches.length;
    const maxSwatches = 11;
    const [showMore, setShowMore] = useState(true);
    const [selectedSizeId, setSelectedSizeId] = useState(sizes[0].id);
    const isSizeDropdownView = defaultSizePickerView === 'Dropdown';
    const formApi = useFormApi();
    const initialSizeState = formApi.getValue('size') || sizes[0].id;
    const enableColorDropdownView = isMobile
        ? true
        : defaultColorPickerView && defaultColorPickerView !== 'Unset'
          ? defaultColorPickerView === 'Colour Dropdown'
          : swatchTotal < MIN_NUMBER_OF_COLORS_FOR_DROPDOWN;

    const organizeBundleColor = (color: string) =>
        setBundleColor &&
        setBundleColor((v: string[]) => (v?.includes(color) ? v.filter((el) => el !== color) : [...v, color]));

    const handleChangeSizeState = (value) => {
        formApi.setValue('size', value);
        setSelectedSizeId(parseInt(value));
    };

    const selectedVariant = currentVariants?.find((variant) => variant.size_id === selectedSizeId);

    const [stockModalId, toggleStockModal] = useState<number | null>(null);

    const notify = useCallback((size: Size) => {
        toggleStockModal(size.simpleProductId);
    }, []);

    const swatchItems = swatches.map((swatch, index) => (
        <SwatchOption
            htmlFor={`swatch-${swatch.value_index}`}
            key={swatch.title}
            optionName={swatch.title}
            labelClassName={classnames(classes.swatchesItemLabel, {
                [classes.swatchesItemHide]: showMore && index >= maxSwatches,
            })}
            input={
                <Checkbox
                    field={`colours.index:${swatch.value_index}`}
                    id={`swatch-${swatch.value_index}`}
                    value={swatch.value_index}
                    data-value={swatch.title}
                    className={classes.swatchesItemInput}
                    onClick={() => organizeBundleColor(swatch.title)}
                />
            }
        >
            <span className={classes.swatchesItem} style={swatchCss(swatch.swatch_data)} data-value={swatch.title}>
                {' '}
                <img src={require('../../../static/icons/ok.png')} alt="" data-img="img-one" />
                <img src={require('../../../static/icons/cancel.png')} alt="" data-img="img-two" />
            </span>
        </SwatchOption>
    ));

    const notCustomizableSizesListComponent = notCustomizableSizesList(sizes);

    const notCustomizableQuantitySelectors = sizes.map((size) => {
        const { stock, id, maxQty } = size;
        const showAlternateStock = stock === StockStatus.OutOfStock;

        return (
            <React.Fragment key={id}>
                <span
                    className={classnames(classes.swatchesListLabel, classes.quantitySelectorLabel, {
                        [classes.quantitySelectorLabelActive]: id === selectedSizeId,
                    })}
                >
                    <span>Quantity</span>
                    <span className={classes.quantitySelectorLabelAvailable}>
                        {!loading &&
                            stock !== StockStatus.OutOfStock &&
                            (maxQty !== null && maxQty < Quantity.Maximum ? (
                                maxQty !== 0 && (
                                    <>
                                        <LazyImg src={require('../../../static/img/check.png')} alt="" />
                                        <strong>{maxQty}</strong> available
                                    </>
                                )
                            ) : (
                                <>
                                    <LazyImg src={require('../../../static/img/check.png')} alt="" /> In Stock
                                </>
                            ))}
                    </span>
                </span>
                <div
                    className={classnames(classes.quantitySelectorWrapper, {
                        [classes.quantitySelectorActive]: id === selectedSizeId,
                    })}
                    key={id}
                >
                    {showAlternateStock ? (
                        <div className={classes.alternativeSkus}>
                            <CallForStockAvailability notify={notify} size={size} />
                            <AlternateStock sku={selectedVariant?.sku} color={selectedVariant?.color_id.toString()} />
                        </div>
                    ) : (
                        <div className={classes.quantitySelector} key={id}>
                            {maxQty === 0 && (
                                <div className={classes.stockInfo}>
                                    <small className={classnames(classes.sizesStock, classes.sizesStockOos)}>
                                        Out of stock. Please choose a different size or call for availability.
                                    </small>
                                </div>
                            )}
                            {maxQty !== 0 &&
                                (maxQty === null ? (
                                    <QtySelect
                                        key="stock-unknown"
                                        id={String(id)}
                                        field={`size:${id}`}
                                        maxValue={0}
                                        useBigButtons
                                        selectCorrectShippingMethods={selectCorrectShippingMethods}
                                    />
                                ) : (
                                    <QtySelect
                                        key="stock-known"
                                        id={String(id)}
                                        field={`size:${id}`}
                                        maxValue={maxQty}
                                        initialValue={0}
                                        useBigButtons
                                        selectCorrectShippingMethods={selectCorrectShippingMethods}
                                    />
                                ))}
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    });

    const sizeSelectItemsComponent = sizeSelectItems(sizes, initialSizeState, handleChangeSizeState);

    return (
        <>
            <div className={classes.swatches}>
                {isBundleView ? (
                    <>
                        <div className={classes.swatchesHeader}>
                            <h2 className={classes.swatchesTitle}>{title}</h2>
                            <small className={classes.swatchesCount} data-test-id="ProductSwatch_SwatchCount">
                                {swatchTotal} Colour{swatchTotal > 1 ? 's' : ''} Available
                            </small>
                        </div>
                        <div className={classes.swatchesList} data-test-id="ProductSwatch_SwatchList">
                            {swatchItems}
                            {swatchTotal > maxSwatches && (
                                <Button
                                    onClick={() => setShowMore(!showMore)}
                                    className={classes.swatchesMore}
                                    variant="light"
                                >
                                    {showMore ? '+ Show more colours' : '- Hide colours'}
                                </Button>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <ColourPicker
                            title="Choose colour"
                            swatches={swatches}
                            enableDropdownView={enableColorDropdownView}
                            resetShippingGroups={resetShippingGroups}
                        />

                        {isSizeDropdownView && (
                            <>
                                {sizes.length > 1 && (
                                    <Selector
                                        title="Choose size"
                                        accessibleName="Choose size"
                                        selectItems={sizeSelectItemsComponent}
                                        selectOptions={notCustomizableSizesListComponent}
                                        handleChangeState={handleChangeSizeState}
                                        initialState={initialSizeState}
                                        field="size"
                                    />
                                )}
                                <div
                                    className={classes.notCustomizableQuantitySelector}
                                    data-loading={String(Boolean(loading))}
                                >
                                    {notCustomizableQuantitySelectors}
                                </div>
                                {afterQuantity}
                            </>
                        )}
                    </>
                )}
            </div>
            {!isSizeDropdownView &&
                !isBundleView &&
                (sizes.length >= 1 ? (
                    <ProductSizes
                        title={<h2 className={classes.sizesTitle}>Size & Quantities</h2>}
                        sizes={sizes}
                        sizeGuide={sizeGuide || ''}
                        fieldPrefix="size"
                        loading={loading}
                        afterList={afterQuantity}
                        notify={notify}
                        sku={selectedVariant?.sku}
                        color={selectedVariant?.color_id.toString()}
                        selectCorrectShippingMethods={selectCorrectShippingMethods}
                        simpleProductsShippingGroups={simpleProductsShippingGroups}
                        parentProductShippingGroup={parentProductShippingGroup}
                    />
                ) : (
                    <>
                        <div
                            className={classes.notCustomizableQuantitySelector}
                            data-loading={String(Boolean(loading))}
                        >
                            {notCustomizableQuantitySelectors}
                        </div>
                        {afterQuantity}
                    </>
                ))}
            {stockModalId && (
                <LazyLoader
                    fallback={null}
                    loader={<LazyStockNotifyModal id={stockModalId} close={() => toggleStockModal(null)} />}
                />
            )}
        </>
    );
};

type ConnectedProps = {
    title?: string;
    count: number;
    swatches: Swatch[];
    sizes: Size[];
    afterQuantity?: React.ReactNode;
    defaultColorPickerView?: string;
    defaultSizePickerView?: string;
    sizeGuide: string;
    loading: boolean;
    currentVariants: ProductVariant[];
    selectCorrectShippingMethods: (sku: string, value: number) => void;
    simpleProductsShippingGroups?: ShippingGroups;
    parentProductShippingGroup?: string;
    resetShippingGroups?: () => void;
};

export const ProductSwatchConnected: React.FC<ConnectedProps> = React.memo((props) => {
    const { getValueStream } = useContext(RxFormContext);
    const dispatch = useDispatch();
    const { replace } = useDeps();
    const field = 'color';

    useEffect(() => {
        /**
         * Get a stream of field changes.
         */
        const value$ = getValueStream().pipe(
            map((s) => s?.[field]),
            distinctUntilChanged(),
            debounceTime(10),
        );

        /**
         * When the field changes, group it with info from the router
         * and decide whether or not to update the url
         */
        const changeStream$ = value$.pipe(
            skip(1),
            tap((value: number) => {
                const { pathname, search } = window.location;
                const params = new URLSearchParams(search);
                if (params.has(field)) {
                    params.set(field, String(value));
                } else {
                    params.append(field, String(value));
                }
                const nextUrl = `${pathname}?${params.toString()}`;
                dispatch(replace(nextUrl));
            }),
        );

        const sub = changeStream$.subscribe();
        return () => {
            sub.unsubscribe();
        };
    }, [dispatch, getValueStream, replace, props]);
    return (
        <ProductSwatch
            title={props.title}
            count={props.count}
            swatches={props.swatches}
            sizes={props.sizes}
            isBundleView={false}
            afterQuantity={props.afterQuantity}
            defaultColorPickerView={props.defaultColorPickerView}
            defaultSizePickerView={props.defaultSizePickerView}
            sizeGuide={props.sizeGuide}
            loading={props.loading}
            currentVariants={props.currentVariants}
            selectCorrectShippingMethods={props.selectCorrectShippingMethods}
            simpleProductsShippingGroups={props.simpleProductsShippingGroups}
            parentProductShippingGroup={props.parentProductShippingGroup}
            resetShippingGroups={props.resetShippingGroups}
        />
    );
});
